import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import DefaultApiLimit from '../../../config/defaultLimit';
import { ByTransactionInterFace } from '../../../interface/by-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableByTransactionFilterApplied,
  fetchByTransactionAccount,
  fetchByTransactionAccountFilters,
  setSortData,
  updateByTransactionFilters,
} from '../../../redux/slices/ByTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
// import { capitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import accountApi from '../../../utils/api/account';

/**
 * By transaction data table component
 * @returns {any} The result
 */
function ByTransactionAccount() {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const {
    ByTransaction,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.byTransaction);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchByTransactionAccount({ accountCustomId, ...data }));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: ByTransaction,
    });

  const column = [
    {
      label: t('station'),
      accessorKey: 'chargingStationName' as keyof ByTransactionInterFace,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Custom station data renderer with side panel open handler
       * @param {any} currentRow - The currentRow
       * @returns {any} The result
       */
      customData: (currentRow: ByTransactionInterFace) => {
        const stationId =
          currentRow.cdrLocation?.id ||
          (typeof (currentRow as any).chargingStation === 'object'
            ? (currentRow as any).chargingStation?._id || (currentRow as any).chargingStation?.id
            : (currentRow as any).chargingStation) ||
          (currentRow as any).stationId ||
          (currentRow as any).chargingStationId;

        return (
          <span
            className={`table-row-each-data ${stationId ? 'hyperlink' : ''}`}
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              if (!stationId) return;
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('stationId', stationId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'ByTransaction',
                  value: currentRow.id || currentRow._id,
                }),
              );
            }}>
            {currentRow.cdrLocation?.id || currentRow.chargingStationName || '-'}
          </span>
        );
      },
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ByTransactionInterFace,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof ByTransactionInterFace,
      sortAllow: true,
      headingDetails: true,
      headingDescription: <>{t('totalAmountDescription')}</>,
      /**
       * Rendering amount with two decimals
       * @param {any} byTransaction - The byTransaction
       * @returns {any} The result
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <p>{ToFixedValue(byTransaction.totalCost.inclVat)}</p>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: '_id' as keyof ByTransactionInterFace,
    },
    {
      label: t('kWhConsumed'),
      accessorKey: 'powerConsumed' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ByTransactionInterFace) => {
        return <span>{currentRow.totalEnergy} kWh</span>;
      },
    },
    // {
    //   label: t('utilityCost'),
    //   accessorKey: 'utilityCost' as keyof ByTransactionInterFace,
    //   sortAllow: false,
    //   /**
    //    * KWh Consumed
    //    * @param {any} currentRow - The currentRow
    //    */
    //   customData: (currentRow: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(currentRow.utilityCost)}</p>
    //   ),
    // },
    // {
    //   label: t('totalUtilityCost'),
    //   accessorKey: 'energyCost' as keyof ByTransactionInterFace,
    //   sortAllow: false,
    //   headingDetails: true,
    //   headingDescription: <>{t('totalUtilityCostDescription')}</>,
    //   /**
    //    * KWh Consumed
    //    * @param {any} currentRow - The currentRow
    //    */
    //   customData: (currentRow: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(currentRow.energyCost)}</p>
    //   ),
    // },
    // {
    //   label: t('pricing'),
    //   accessorKey: 'pricing' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   /**
    //    * Rendering pricing with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction?.pricing?.price)}</p>
    //   ),
    // },
    // {
    //   label: t('totalCostPricing'),
    //   accessorKey: 'totalEnergyAmount' as keyof ByTransactionInterFace,
    //   sortAllow: false,
    //   headingDetails: true,
    //   headingDescription: <>{t('totalPowerCostDescription')}</>,
    //   /**
    //    * Rendering pricing with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction?.totalEnergyAmount)}</p>
    //   ),
    // },
    // {
    //   label: t('adminShare'),
    //   accessorKey: 'adminRevenue' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   headingDetails: true,
    //   headingDescription: <>{t('adminSharePercentDescription')}</>,
    //   /**
    //    * Rendering admin share with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.adminRevenue)}</p>
    //   ),
    // },
    // {
    //   label: t('adminSharePercent'),
    //   accessorKey: 'adminRevenuePercentage' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   /**
    //    * Rendering admin share with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.adminRevenuePercentage)}</p>
    //   ),
    // },
    // {
    //   label: t('facilityShare'),
    //   accessorKey: 'foRevenue' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   headingDetails: true,
    //   headingDescription: <>{t('facilitySharePercentDescription')}</>,
    //   /**
    //    * Rendering admin share with two decimal
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.foRevenue)}</p>
    //   ),
    // },
    // {
    //   label: t('facilitySharePercent'),
    //   accessorKey: 'foRevenuePercentage' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   /**
    //    * Rendering admin share with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.foRevenuePercentage)}</p>
    //   ),
    // },
    // {
    //   label: t('idleFee'),
    //   accessorKey: 'penaltyAmount' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   /**
    //    * Rendering admin share with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.penaltyAmount)}</p>
    //   ),
    // },
    // {
    //   label: t('platformFee'),
    //   accessorKey: 'platformFee' as keyof ByTransactionInterFace,
    //   sortAllow: false,
    //   /**
    //    * Rendering platform fee with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.platformAmount)}</p>
    //   ),
    // },
    // {
    //   label: t('sessionFee'),
    //   accessorKey: 'sessionAmount' as keyof ByTransactionInterFace,
    //   sortAllow: true,
    //   /**
    //    * Rendering session fee with two decimals
    //    * @param {any} byTransaction - The byTransaction
    //    * @returns {any} The result
    //    */
    //   customData: (byTransaction: ByTransactionInterFace) => (
    //     <p>{ToFixedValue(byTransaction.sessionAmount)}</p>
    //   ),
    // },
    // {
    //   label: t('paymentMethod'),
    //   accessorKey: 'paymentMethod' as keyof ByTransactionInterFace,
    // },
    // {
    //   label: t('paymentStatus'),
    //   accessorKey: 'paymentStatus' as keyof ByTransactionInterFace,
    //   /**
    //    * Payment status view in table
    //    * @param {any} payoutsStatus - The payoutsStatus
    //    * @returns {any} The result
    //    */
    //   customData: (payoutsStatus: ByTransactionInterFace) => {
    //     const statusText = payoutsStatus.paymentStatus
    //       ? capitalizeFirstLetter(payoutsStatus.paymentStatus)
    //       : '';

    //     return (
    //       <div className="status-in-payout-table">
    //         <p
    //           className={`${
    //             payoutsStatus.paymentStatus === 'success' ||
    //             payoutsStatus.paymentStatus === 'processed'
    //               ? 'success-text'
    //               : 'failed'
    //           }`}>
    //           {statusText}
    //         </p>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   label: t('guestUserTransaction'),
    //   accessorKey: 'isFreeTransaction' as keyof ByTransactionInterFace,
    //   /**
    //    * Payment status view in table
    //    * @param {any} payoutsStatus - The payoutsStatus
    //    * @returns {any} The result
    //    */
    //   customData: (payoutsStatus: ByTransactionInterFace) =>
    //     payoutsStatus.isFreeTransaction ? <>{t('yes')}</> : <>{t('no')}</>,
    // },
  ];

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Export data function for pdf and csv
   * @param {string} type - Export format ('csv' | 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (!accountCustomId) return;
    const query: Record<string, any> = {
      ...queryParameters,
      accountCustomId,
    };
    if (sortBy && sortField) {
      query.sortBy = sortBy;
      query.sortField = sortField;
    }
    setExportLoader(true);
    accountApi
      .fetchByTransactionExportData(query)
      .then((response) => {
        const visibleColumns = column.filter(
          (col) => !hiddenColumns.includes(col.accessorKey as string),
        );

        const headers = [
          t('sNo', 'S.No'),
          ...visibleColumns.map((col) => col.label),
          ...(!hiddenColumns.includes('createdAt') ? [t('created', 'Created')] : []),
          ...(!hiddenColumns.includes('updatedAt') ? [t('lastUpdated', 'Last Updated')] : []),
        ];

        const rows = response.data.map((row: ByTransactionInterFace, index: number) => [
          index + 1,
          ...visibleColumns.map((col) => {
            if (col.accessorKey === 'chargingStationName') {
              return (
                row.chargingStationName ||
                row.cdrLocation?.id ||
                (typeof (row as any).chargingStation === 'object'
                  ? (row as any).chargingStation?.name || (row as any).chargingStation?._id
                  : (row as any).chargingStation) ||
                t('notAvailable')
              );
            }
            if (col.accessorKey === 'chargingPointSn') {
              return row.chargingPointSn || (row as any).chargingPoint || t('notAvailable');
            }
            if (col.accessorKey === 'amount') {
              const amount =
                (row as any).totalCost?.inclVat ??
                (row as any).totalCost?.exclVat ??
                row.amount ??
                0;
              return ToFixedValue(amount as any);
            }
            if (col.accessorKey === '_id') {
              return row._id || row.id || (row as any).transactionId || t('notAvailable');
            }
            if (col.accessorKey === 'powerConsumed') {
              const energy =
                (row as any).totalEnergy ??
                (row as any).energyConsumed ??
                row.powerConsumed ??
                0;
              return `${ToFixedValue(energy as any, 3)} kWh`;
            }
            let value = row[col.accessorKey];
            if (col?.accessorKey === 'userEmail' && !value) {
              value = (row as any)?.email;
            }
            return value !== undefined && value !== null && value !== ''
              ? value
              : t('notAvailable');
          }),
          ...(!hiddenColumns.includes('createdAt')
            ? [ConvertUTCtoDateTime(row.createdAt) || t('notAvailable')]
            : []),
          ...(!hiddenColumns.includes('updatedAt')
            ? [ConvertUTCtoDateTime((row as any).updatedAt) || t('notAvailable')]
            : []),
        ]);

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Render the date filters
   * @returns {any} The result
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
        />
      </div>
    );
  };

  useEffect(() => {
    if (
      isFilterUpdated ||
      ByTransaction.length === 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      accountCustomId
    ) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        accountCustomId,
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
        sortBy,
        sortField,
      });
      dispatch(disableByTransactionFilterApplied());
    }
  }, [filters, dateRange.endDate, dateRange.startDate, accountCustomId]);

  return (
    <div>
      <CustomTable
        version="v2"
        header={column}
        data={ByTransaction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        pageLimitChange={pageNateLimitChange}
        handleSortChange={handleSortFunction}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateByTransactionFilters}
        filterListLoading={filterListLoading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        filterListToDispatch={fetchByTransactionAccountFilters}
        exportLoader={exportLoader}
        exportData={ByTransaction}
        refershCurrentPages={handleRefresh}
        refreshRequired
        hoverLabel={t('refresh')}
        sizeChangeButtonRequired
        height={600}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        created
        lastUpdated
      />
    </div>
  );
}

export default ByTransactionAccount;
